// Database types for Bilbase Danmark — Elbil-platform

export type BodyType = 'Hatchback' | 'Sedan' | 'Station Wagon' | 'Stationscar' | 'SUV' | 'Crossover' | 'Coupe' | 'Convertible' | 'MPV' | 'Minibil' | 'Fastback' | 'Van' | 'Pickup' | 'Other';
export type PowertrainType = 'EV';
export type Drivetrain = 'FWD' | 'RWD' | 'AWD';
export type Transmission = 'Manual' | 'Automatic' | 'N/A';
export type PriceType = 'List' | 'Campaign' | 'From' | 'LeaseExample';
export type ReviewFormat = 'Video' | 'Article';
export type ReviewLanguage = 'DA' | 'EN' | 'Other';
export type AppRole = 'admin' | 'user';
export type AvailabilityStatus = 'on_sale' | 'upcoming' | 'discontinued';

export interface Brand {
  id: string;
  name: string;
  country?: string;
  parent_company?: string;
  description?: string;
  website_url?: string;
  logo_url?: string;
  slug: string;
  created_at?: string;
  updated_at?: string;
}

export interface Model {
  id: string;
  brand_id: string;
  name: string;
  body_type: BodyType;
  description?: string;
  image_url?: string;
  is_active: boolean;
  slug: string;
  created_at?: string;
  updated_at?: string;
  brand?: Brand;
}

export interface Variant {
  id: string;
  model_id: string;
  name: string;
  powertrain_type: PowertrainType;
  drivetrain: Drivetrain;
  transmission: Transmission;
  seats: number;
  doors: number;
  curb_weight_kg?: number;
  trunk_l?: number;
  tow_kg_braked?: number;
  tow_kg_unbraked?: number;
  notes?: string;
  slug: string;
  is_active: boolean;
  total_weight_kg?: number;
  speaker_brand?: string;
  model_year?: number;
  created_at?: string;
  updated_at?: string;
  model?: Model;
  powertrain_specs?: PowertrainSpecs;
  prices_dk?: PriceDK[];
}

export interface PowertrainSpecs {
  id: string;
  variant_id: string;
  power_hp?: number;
  torque_nm?: number;
  battery_kwh?: number;
  battery_type?: string;
  range_wltp_km?: number;
  consumption_wh_km?: number;
  charging_ac_kw?: number;
  charging_dc_kw?: number;
  dc_charge_10_80_min?: number;
  acceleration_0_100?: number;
  top_speed_kmh?: number;
  created_at?: string;
  updated_at?: string;
}

export interface PriceDK {
  id: string;
  variant_id: string;
  price_dkk: number;
  price_type: PriceType;
  effective_date: string;
  source_note?: string;
  last_updated?: string;
  created_at?: string;
}

export interface ReviewLink {
  id: string;
  model_id?: string;
  variant_id?: string;
  title: string;
  url: string;
  publisher: string;
  format: ReviewFormat;
  language: ReviewLanguage;
  publish_date?: string;
  summary?: string;
  created_at?: string;
  updated_at?: string;
}

export interface NewsPost {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  content?: string;
  cover_image_url?: string;
  published_at?: string;
  is_published: boolean;
  pinned: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface UserRole {
  id: string;
  user_id: string;
  role: AppRole;
  created_at?: string;
}

export interface CarSearchFiltersType {
  query?: string;
  maxPrice?: number;
  bodyType?: BodyType[];
  minRange?: number;
  minBattery?: number;
  minTrunk?: number;
  minTowing?: number;
  brandId?: string;
}

export interface CarSearchResult {
  variant: Variant;
  model: Model;
  brand: Brand;
  lowestPrice?: PriceDK;
  specs?: PowertrainSpecs;
}

export type SortOption = 'price_asc' | 'price_desc' | 'range_desc' | 'power_desc' | 'battery_desc' | 'consumption_asc' | 'charging_dc_desc' | 'updated_desc';

export const bodyTypeLabels: Record<BodyType, string> = {
  'Hatchback': 'Hatchback',
  'Sedan': 'Sedan',
  'Station Wagon': 'Stationcar',
  'Stationscar': 'Stationcar',
  'SUV': 'SUV',
  'Crossover': 'Crossover',
  'Coupe': 'Coupé',
  'Convertible': 'Cabriolet',
  'MPV': 'MPV',
  'Minibil': 'Minibil',
  'Fastback': 'Fastback',
  'Van': 'Van',
  'Pickup': 'Pickup',
  'Other': 'Andet',
};

export const powertrainLabels: Record<PowertrainType, string> = {
  'EV': 'El',
};

export const getPowertrainBadgeVariant = (_powertrain: string): 'ev' => 'ev';

export const powertrainUIOptions: { value: PowertrainType; label: string }[] = [
  { value: 'EV', label: 'El' },
];

export const drivetrainLabels: Record<Drivetrain, string> = {
  'FWD': 'Forhjulstræk',
  'RWD': 'Baghjulstræk',
  'AWD': 'Firehjulstræk',
};

export const transmissionLabels: Record<Transmission, string> = {
  'Manual': 'Manuel',
  'Automatic': 'Automatisk',
  'N/A': 'Ikke relevant',
};

export const priceTypeLabels: Record<PriceType, string> = {
  'List': 'Listepris',
  'Campaign': 'Kampagnepris',
  'From': 'Fra-pris',
  'LeaseExample': 'Leasingeksempel',
};

export const reviewFormatLabels: Record<ReviewFormat, string> = {
  'Video': 'Video',
  'Article': 'Artikel',
};

export const reviewLanguageLabels: Record<ReviewLanguage, string> = {
  'DA': 'Dansk',
  'EN': 'Engelsk',
  'Other': 'Andet',
};

export const availabilityLabels: Record<AvailabilityStatus, string> = {
  'on_sale': 'På markedet',
  'upcoming': 'Kommer',
  'discontinued': 'Udgået',
};
